import {
  prepareEvent,
  prepareContractCall,
  readContract,
  type BaseTransactionOptions,
  type AbiParameterToPrimitiveType,
} from "thirdweb";

/**
* Contract events
*/

/**
 * Represents the filters for the "AssetAdded" event.
 */
export type AssetAddedEventFilters = Partial<{
  newAsset: AbiParameterToPrimitiveType<{"indexed":true,"internalType":"address","name":"newAsset","type":"address"}>
}>;

/**
 * Creates an event object for the AssetAdded event.
 * @param filters - Optional filters to apply to the event.
 * @returns The prepared event object.
 * @example
 * ```
 * import { getContractEvents } from "thirdweb";
 * import { assetAddedEvent } from "TODO";
 *
 * const events = await getContractEvents({
 * contract,
 * events: [
 *  assetAddedEvent({
 *  newAsset: ...,
 * })
 * ],
 * });
 * ```
 */
export function assetAddedEvent(filters: AssetAddedEventFilters = {}) {
  return prepareEvent({
    signature: "event AssetAdded(address indexed newAsset, address bundle)",
    filters,
  });
};
  

/**
 * Represents the filters for the "AssetRemoved" event.
 */
export type AssetRemovedEventFilters = Partial<{
  asset: AbiParameterToPrimitiveType<{"indexed":true,"internalType":"address","name":"asset","type":"address"}>
}>;

/**
 * Creates an event object for the AssetRemoved event.
 * @param filters - Optional filters to apply to the event.
 * @returns The prepared event object.
 * @example
 * ```
 * import { getContractEvents } from "thirdweb";
 * import { assetRemovedEvent } from "TODO";
 *
 * const events = await getContractEvents({
 * contract,
 * events: [
 *  assetRemovedEvent({
 *  asset: ...,
 * })
 * ],
 * });
 * ```
 */
export function assetRemovedEvent(filters: AssetRemovedEventFilters = {}) {
  return prepareEvent({
    signature: "event AssetRemoved(address indexed asset)",
    filters,
  });
};
  



/**
 * Creates an event object for the FeesChanged event.
 * @returns The prepared event object.
 * @example
 * ```
 * import { getContractEvents } from "thirdweb";
 * import { feesChangedEvent } from "TODO";
 *
 * const events = await getContractEvents({
 * contract,
 * events: [
 *  feesChangedEvent()
 * ],
 * });
 * ```
 */
export function feesChangedEvent() {
  return prepareEvent({
    signature: "event FeesChanged((address feeReceiver, uint256 depositFeePercent, uint256 withdrawalFeePercent, uint256 annualFeePercent) newFees)",
  });
};
  



/**
 * Creates an event object for the Initialized event.
 * @returns The prepared event object.
 * @example
 * ```
 * import { getContractEvents } from "thirdweb";
 * import { initializedEvent } from "TODO";
 *
 * const events = await getContractEvents({
 * contract,
 * events: [
 *  initializedEvent()
 * ],
 * });
 * ```
 */
export function initializedEvent() {
  return prepareEvent({
    signature: "event Initialized(uint64 version)",
  });
};
  



/**
 * Creates an event object for the MaxArraySizeChanged event.
 * @returns The prepared event object.
 * @example
 * ```
 * import { getContractEvents } from "thirdweb";
 * import { maxArraySizeChangedEvent } from "TODO";
 *
 * const events = await getContractEvents({
 * contract,
 * events: [
 *  maxArraySizeChangedEvent()
 * ],
 * });
 * ```
 */
export function maxArraySizeChangedEvent() {
  return prepareEvent({
    signature: "event MaxArraySizeChanged(uint256 newMaxArraySize)",
  });
};
  

/**
 * Represents the filters for the "OwnershipHandoverCanceled" event.
 */
export type OwnershipHandoverCanceledEventFilters = Partial<{
  pendingOwner: AbiParameterToPrimitiveType<{"indexed":true,"internalType":"address","name":"pendingOwner","type":"address"}>
}>;

/**
 * Creates an event object for the OwnershipHandoverCanceled event.
 * @param filters - Optional filters to apply to the event.
 * @returns The prepared event object.
 * @example
 * ```
 * import { getContractEvents } from "thirdweb";
 * import { ownershipHandoverCanceledEvent } from "TODO";
 *
 * const events = await getContractEvents({
 * contract,
 * events: [
 *  ownershipHandoverCanceledEvent({
 *  pendingOwner: ...,
 * })
 * ],
 * });
 * ```
 */
export function ownershipHandoverCanceledEvent(filters: OwnershipHandoverCanceledEventFilters = {}) {
  return prepareEvent({
    signature: "event OwnershipHandoverCanceled(address indexed pendingOwner)",
    filters,
  });
};
  

/**
 * Represents the filters for the "OwnershipHandoverRequested" event.
 */
export type OwnershipHandoverRequestedEventFilters = Partial<{
  pendingOwner: AbiParameterToPrimitiveType<{"indexed":true,"internalType":"address","name":"pendingOwner","type":"address"}>
}>;

/**
 * Creates an event object for the OwnershipHandoverRequested event.
 * @param filters - Optional filters to apply to the event.
 * @returns The prepared event object.
 * @example
 * ```
 * import { getContractEvents } from "thirdweb";
 * import { ownershipHandoverRequestedEvent } from "TODO";
 *
 * const events = await getContractEvents({
 * contract,
 * events: [
 *  ownershipHandoverRequestedEvent({
 *  pendingOwner: ...,
 * })
 * ],
 * });
 * ```
 */
export function ownershipHandoverRequestedEvent(filters: OwnershipHandoverRequestedEventFilters = {}) {
  return prepareEvent({
    signature: "event OwnershipHandoverRequested(address indexed pendingOwner)",
    filters,
  });
};
  

/**
 * Represents the filters for the "OwnershipTransferred" event.
 */
export type OwnershipTransferredEventFilters = Partial<{
  oldOwner: AbiParameterToPrimitiveType<{"indexed":true,"internalType":"address","name":"oldOwner","type":"address"}>
newOwner: AbiParameterToPrimitiveType<{"indexed":true,"internalType":"address","name":"newOwner","type":"address"}>
}>;

/**
 * Creates an event object for the OwnershipTransferred event.
 * @param filters - Optional filters to apply to the event.
 * @returns The prepared event object.
 * @example
 * ```
 * import { getContractEvents } from "thirdweb";
 * import { ownershipTransferredEvent } from "TODO";
 *
 * const events = await getContractEvents({
 * contract,
 * events: [
 *  ownershipTransferredEvent({
 *  oldOwner: ...,
 *  newOwner: ...,
 * })
 * ],
 * });
 * ```
 */
export function ownershipTransferredEvent(filters: OwnershipTransferredEventFilters = {}) {
  return prepareEvent({
    signature: "event OwnershipTransferred(address indexed oldOwner, address indexed newOwner)",
    filters,
  });
};
  



/**
 * Creates an event object for the PercentageToDissolveChanged event.
 * @returns The prepared event object.
 * @example
 * ```
 * import { getContractEvents } from "thirdweb";
 * import { percentageToDissolveChangedEvent } from "TODO";
 *
 * const events = await getContractEvents({
 * contract,
 * events: [
 *  percentageToDissolveChangedEvent()
 * ],
 * });
 * ```
 */
export function percentageToDissolveChangedEvent() {
  return prepareEvent({
    signature: "event PercentageToDissolveChanged(uint256 newPercentage)",
  });
};
  

/**
* Contract read functions
*/



/**
 * Calls the "ONE_HUNDRED_PERCENT" function on the contract.
 * @param options - The options for the ONE_HUNDRED_PERCENT function.
 * @returns The parsed result of the function call.
 * @example
 * ```
 * import { ONE_HUNDRED_PERCENT } from "TODO";
 *
 * const result = await ONE_HUNDRED_PERCENT();
 *
 * ```
 */
export async function ONE_HUNDRED_PERCENT(
  options: BaseTransactionOptions
) {
  return readContract({
    contract: options.contract,
    method: [
  "0xdd0081c7",
  [],
  [
    {
      "internalType": "uint256",
      "name": "",
      "type": "uint256"
    }
  ]
],
    params: []
  });
};




/**
 * Calls the "ONE_HUNDRED_PERCENT_ANNUALLY" function on the contract.
 * @param options - The options for the ONE_HUNDRED_PERCENT_ANNUALLY function.
 * @returns The parsed result of the function call.
 * @example
 * ```
 * import { ONE_HUNDRED_PERCENT_ANNUALLY } from "TODO";
 *
 * const result = await ONE_HUNDRED_PERCENT_ANNUALLY();
 *
 * ```
 */
export async function ONE_HUNDRED_PERCENT_ANNUALLY(
  options: BaseTransactionOptions
) {
  return readContract({
    contract: options.contract,
    method: [
  "0x51aa3e57",
  [],
  [
    {
      "internalType": "uint256",
      "name": "",
      "type": "uint256"
    }
  ]
],
    params: []
  });
};




/**
 * Calls the "annualFeePercent" function on the contract.
 * @param options - The options for the annualFeePercent function.
 * @returns The parsed result of the function call.
 * @example
 * ```
 * import { annualFeePercent } from "TODO";
 *
 * const result = await annualFeePercent();
 *
 * ```
 */
export async function annualFeePercent(
  options: BaseTransactionOptions
) {
  return readContract({
    contract: options.contract,
    method: [
  "0x5962203a",
  [],
  [
    {
      "internalType": "uint256",
      "name": "",
      "type": "uint256"
    }
  ]
],
    params: []
  });
};


/**
 * Represents the parameters for the "assetsBundle" function.
 */
export type AssetsBundleParams = {
  asset: AbiParameterToPrimitiveType<{"internalType":"address","name":"asset","type":"address"}>
};

/**
 * Calls the "assetsBundle" function on the contract.
 * @param options - The options for the assetsBundle function.
 * @returns The parsed result of the function call.
 * @example
 * ```
 * import { assetsBundle } from "TODO";
 *
 * const result = await assetsBundle({
 *  asset: ...,
 * });
 *
 * ```
 */
export async function assetsBundle(
  options: BaseTransactionOptions<AssetsBundleParams>
) {
  return readContract({
    contract: options.contract,
    method: [
  "0x26944d1f",
  [
    {
      "internalType": "address",
      "name": "asset",
      "type": "address"
    }
  ],
  [
    {
      "internalType": "address",
      "name": "bundle",
      "type": "address"
    }
  ]
],
    params: [options.asset]
  });
};




/**
 * Calls the "depositFeePercent" function on the contract.
 * @param options - The options for the depositFeePercent function.
 * @returns The parsed result of the function call.
 * @example
 * ```
 * import { depositFeePercent } from "TODO";
 *
 * const result = await depositFeePercent();
 *
 * ```
 */
export async function depositFeePercent(
  options: BaseTransactionOptions
) {
  return readContract({
    contract: options.contract,
    method: [
  "0xcc1252ae",
  [],
  [
    {
      "internalType": "uint256",
      "name": "",
      "type": "uint256"
    }
  ]
],
    params: []
  });
};




/**
 * Calls the "dissolvePercentage" function on the contract.
 * @param options - The options for the dissolvePercentage function.
 * @returns The parsed result of the function call.
 * @example
 * ```
 * import { dissolvePercentage } from "TODO";
 *
 * const result = await dissolvePercentage();
 *
 * ```
 */
export async function dissolvePercentage(
  options: BaseTransactionOptions
) {
  return readContract({
    contract: options.contract,
    method: [
  "0x5e2aa261",
  [],
  [
    {
      "internalType": "uint256",
      "name": "",
      "type": "uint256"
    }
  ]
],
    params: []
  });
};




/**
 * Calls the "feeReceiver" function on the contract.
 * @param options - The options for the feeReceiver function.
 * @returns The parsed result of the function call.
 * @example
 * ```
 * import { feeReceiver } from "TODO";
 *
 * const result = await feeReceiver();
 *
 * ```
 */
export async function feeReceiver(
  options: BaseTransactionOptions
) {
  return readContract({
    contract: options.contract,
    method: [
  "0xb3f00674",
  [],
  [
    {
      "internalType": "address",
      "name": "",
      "type": "address"
    }
  ]
],
    params: []
  });
};




/**
 * Calls the "maxArraySize" function on the contract.
 * @param options - The options for the maxArraySize function.
 * @returns The parsed result of the function call.
 * @example
 * ```
 * import { maxArraySize } from "TODO";
 *
 * const result = await maxArraySize();
 *
 * ```
 */
export async function maxArraySize(
  options: BaseTransactionOptions
) {
  return readContract({
    contract: options.contract,
    method: [
  "0xca7628bb",
  [],
  [
    {
      "internalType": "uint256",
      "name": "",
      "type": "uint256"
    }
  ]
],
    params: []
  });
};




/**
 * Calls the "owner" function on the contract.
 * @param options - The options for the owner function.
 * @returns The parsed result of the function call.
 * @example
 * ```
 * import { owner } from "TODO";
 *
 * const result = await owner();
 *
 * ```
 */
export async function owner(
  options: BaseTransactionOptions
) {
  return readContract({
    contract: options.contract,
    method: [
  "0x8da5cb5b",
  [],
  [
    {
      "internalType": "address",
      "name": "result",
      "type": "address"
    }
  ]
],
    params: []
  });
};


/**
 * Represents the parameters for the "ownershipHandoverExpiresAt" function.
 */
export type OwnershipHandoverExpiresAtParams = {
  pendingOwner: AbiParameterToPrimitiveType<{"internalType":"address","name":"pendingOwner","type":"address"}>
};

/**
 * Calls the "ownershipHandoverExpiresAt" function on the contract.
 * @param options - The options for the ownershipHandoverExpiresAt function.
 * @returns The parsed result of the function call.
 * @example
 * ```
 * import { ownershipHandoverExpiresAt } from "TODO";
 *
 * const result = await ownershipHandoverExpiresAt({
 *  pendingOwner: ...,
 * });
 *
 * ```
 */
export async function ownershipHandoverExpiresAt(
  options: BaseTransactionOptions<OwnershipHandoverExpiresAtParams>
) {
  return readContract({
    contract: options.contract,
    method: [
  "0xfee81cf4",
  [
    {
      "internalType": "address",
      "name": "pendingOwner",
      "type": "address"
    }
  ],
  [
    {
      "internalType": "uint256",
      "name": "result",
      "type": "uint256"
    }
  ]
],
    params: [options.pendingOwner]
  });
};




/**
 * Calls the "withdrawalFeePercent" function on the contract.
 * @param options - The options for the withdrawalFeePercent function.
 * @returns The parsed result of the function call.
 * @example
 * ```
 * import { withdrawalFeePercent } from "TODO";
 *
 * const result = await withdrawalFeePercent();
 *
 * ```
 */
export async function withdrawalFeePercent(
  options: BaseTransactionOptions
) {
  return readContract({
    contract: options.contract,
    method: [
  "0xa0499b3b",
  [],
  [
    {
      "internalType": "uint256",
      "name": "",
      "type": "uint256"
    }
  ]
],
    params: []
  });
};


/**
* Contract write functions
*/

/**
 * Represents the parameters for the "addAssetsBundle" function.
 */
export type AddAssetsBundleParams = {
  assets: AbiParameterToPrimitiveType<{"internalType":"address[]","name":"assets","type":"address[]"}>
bundles: AbiParameterToPrimitiveType<{"internalType":"address[]","name":"bundles","type":"address[]"}>
};

/**
 * Calls the "addAssetsBundle" function on the contract.
 * @param options - The options for the "addAssetsBundle" function.
 * @returns A prepared transaction object.
 * @example
 * ```
 * import { addAssetsBundle } from "TODO";
 *
 * const transaction = addAssetsBundle({
 *  assets: ...,
 *  bundles: ...,
 * });
 *
 * // Send the transaction
 * ...
 *
 * ```
 */
export function addAssetsBundle(
  options: BaseTransactionOptions<AddAssetsBundleParams>
) {
  return prepareContractCall({
    contract: options.contract,
    method: [
  "0xb8a56eef",
  [
    {
      "internalType": "address[]",
      "name": "assets",
      "type": "address[]"
    },
    {
      "internalType": "address[]",
      "name": "bundles",
      "type": "address[]"
    }
  ],
  []
],
    params: [options.assets, options.bundles]
  });
};




/**
 * Calls the "cancelOwnershipHandover" function on the contract.
 * @param options - The options for the "cancelOwnershipHandover" function.
 * @returns A prepared transaction object.
 * @example
 * ```
 * import { cancelOwnershipHandover } from "TODO";
 *
 * const transaction = cancelOwnershipHandover();
 *
 * // Send the transaction
 * ...
 *
 * ```
 */
export function cancelOwnershipHandover(
  options: BaseTransactionOptions
) {
  return prepareContractCall({
    contract: options.contract,
    method: [
  "0x54d1f13d",
  [],
  []
],
    params: []
  });
};


/**
 * Represents the parameters for the "changeDissolvePercentage" function.
 */
export type ChangeDissolvePercentageParams = {
  newPercentage: AbiParameterToPrimitiveType<{"internalType":"uint256","name":"newPercentage","type":"uint256"}>
};

/**
 * Calls the "changeDissolvePercentage" function on the contract.
 * @param options - The options for the "changeDissolvePercentage" function.
 * @returns A prepared transaction object.
 * @example
 * ```
 * import { changeDissolvePercentage } from "TODO";
 *
 * const transaction = changeDissolvePercentage({
 *  newPercentage: ...,
 * });
 *
 * // Send the transaction
 * ...
 *
 * ```
 */
export function changeDissolvePercentage(
  options: BaseTransactionOptions<ChangeDissolvePercentageParams>
) {
  return prepareContractCall({
    contract: options.contract,
    method: [
  "0xbf12cc6c",
  [
    {
      "internalType": "uint256",
      "name": "newPercentage",
      "type": "uint256"
    }
  ],
  []
],
    params: [options.newPercentage]
  });
};


/**
 * Represents the parameters for the "changeMaxArraySize" function.
 */
export type ChangeMaxArraySizeParams = {
  newMaxArraySize: AbiParameterToPrimitiveType<{"internalType":"uint256","name":"newMaxArraySize","type":"uint256"}>
};

/**
 * Calls the "changeMaxArraySize" function on the contract.
 * @param options - The options for the "changeMaxArraySize" function.
 * @returns A prepared transaction object.
 * @example
 * ```
 * import { changeMaxArraySize } from "TODO";
 *
 * const transaction = changeMaxArraySize({
 *  newMaxArraySize: ...,
 * });
 *
 * // Send the transaction
 * ...
 *
 * ```
 */
export function changeMaxArraySize(
  options: BaseTransactionOptions<ChangeMaxArraySizeParams>
) {
  return prepareContractCall({
    contract: options.contract,
    method: [
  "0x62eeecbc",
  [
    {
      "internalType": "uint256",
      "name": "newMaxArraySize",
      "type": "uint256"
    }
  ],
  []
],
    params: [options.newMaxArraySize]
  });
};


/**
 * Represents the parameters for the "completeOwnershipHandover" function.
 */
export type CompleteOwnershipHandoverParams = {
  pendingOwner: AbiParameterToPrimitiveType<{"internalType":"address","name":"pendingOwner","type":"address"}>
};

/**
 * Calls the "completeOwnershipHandover" function on the contract.
 * @param options - The options for the "completeOwnershipHandover" function.
 * @returns A prepared transaction object.
 * @example
 * ```
 * import { completeOwnershipHandover } from "TODO";
 *
 * const transaction = completeOwnershipHandover({
 *  pendingOwner: ...,
 * });
 *
 * // Send the transaction
 * ...
 *
 * ```
 */
export function completeOwnershipHandover(
  options: BaseTransactionOptions<CompleteOwnershipHandoverParams>
) {
  return prepareContractCall({
    contract: options.contract,
    method: [
  "0xf04e283e",
  [
    {
      "internalType": "address",
      "name": "pendingOwner",
      "type": "address"
    }
  ],
  []
],
    params: [options.pendingOwner]
  });
};


/**
 * Represents the parameters for the "feesSetUp" function.
 */
export type FeesSetUpParams = {
  feeReceiver: AbiParameterToPrimitiveType<{"internalType":"address","name":"_feeReceiver","type":"address"}>
depositFeePercent: AbiParameterToPrimitiveType<{"internalType":"uint256","name":"_depositFeePercent","type":"uint256"}>
withdrawalFeePercent: AbiParameterToPrimitiveType<{"internalType":"uint256","name":"_withdrawalFeePercent","type":"uint256"}>
annualFeePercent: AbiParameterToPrimitiveType<{"internalType":"uint256","name":"_annualFeePercent","type":"uint256"}>
};

/**
 * Calls the "feesSetUp" function on the contract.
 * @param options - The options for the "feesSetUp" function.
 * @returns A prepared transaction object.
 * @example
 * ```
 * import { feesSetUp } from "TODO";
 *
 * const transaction = feesSetUp({
 *  feeReceiver: ...,
 *  depositFeePercent: ...,
 *  withdrawalFeePercent: ...,
 *  annualFeePercent: ...,
 * });
 *
 * // Send the transaction
 * ...
 *
 * ```
 */
export function feesSetUp(
  options: BaseTransactionOptions<FeesSetUpParams>
) {
  return prepareContractCall({
    contract: options.contract,
    method: [
  "0xfbfa96d5",
  [
    {
      "internalType": "address",
      "name": "_feeReceiver",
      "type": "address"
    },
    {
      "internalType": "uint256",
      "name": "_depositFeePercent",
      "type": "uint256"
    },
    {
      "internalType": "uint256",
      "name": "_withdrawalFeePercent",
      "type": "uint256"
    },
    {
      "internalType": "uint256",
      "name": "_annualFeePercent",
      "type": "uint256"
    }
  ],
  []
],
    params: [options.feeReceiver, options.depositFeePercent, options.withdrawalFeePercent, options.annualFeePercent]
  });
};


/**
 * Represents the parameters for the "initialize" function.
 */
export type InitializeParams = {
  owner: AbiParameterToPrimitiveType<{"internalType":"address","name":"_owner","type":"address"}>
};

/**
 * Calls the "initialize" function on the contract.
 * @param options - The options for the "initialize" function.
 * @returns A prepared transaction object.
 * @example
 * ```
 * import { initialize } from "TODO";
 *
 * const transaction = initialize({
 *  owner: ...,
 * });
 *
 * // Send the transaction
 * ...
 *
 * ```
 */
export function initialize(
  options: BaseTransactionOptions<InitializeParams>
) {
  return prepareContractCall({
    contract: options.contract,
    method: [
  "0xc4d66de8",
  [
    {
      "internalType": "address",
      "name": "_owner",
      "type": "address"
    }
  ],
  []
],
    params: [options.owner]
  });
};


/**
 * Represents the parameters for the "removeAssetBundle" function.
 */
export type RemoveAssetBundleParams = {
  assetAddress: AbiParameterToPrimitiveType<{"internalType":"address","name":"assetAddress","type":"address"}>
};

/**
 * Calls the "removeAssetBundle" function on the contract.
 * @param options - The options for the "removeAssetBundle" function.
 * @returns A prepared transaction object.
 * @example
 * ```
 * import { removeAssetBundle } from "TODO";
 *
 * const transaction = removeAssetBundle({
 *  assetAddress: ...,
 * });
 *
 * // Send the transaction
 * ...
 *
 * ```
 */
export function removeAssetBundle(
  options: BaseTransactionOptions<RemoveAssetBundleParams>
) {
  return prepareContractCall({
    contract: options.contract,
    method: [
  "0x1d2d4d58",
  [
    {
      "internalType": "address",
      "name": "assetAddress",
      "type": "address"
    }
  ],
  []
],
    params: [options.assetAddress]
  });
};




/**
 * Calls the "renounceOwnership" function on the contract.
 * @param options - The options for the "renounceOwnership" function.
 * @returns A prepared transaction object.
 * @example
 * ```
 * import { renounceOwnership } from "TODO";
 *
 * const transaction = renounceOwnership();
 *
 * // Send the transaction
 * ...
 *
 * ```
 */
export function renounceOwnership(
  options: BaseTransactionOptions
) {
  return prepareContractCall({
    contract: options.contract,
    method: [
  "0x715018a6",
  [],
  []
],
    params: []
  });
};




/**
 * Calls the "requestOwnershipHandover" function on the contract.
 * @param options - The options for the "requestOwnershipHandover" function.
 * @returns A prepared transaction object.
 * @example
 * ```
 * import { requestOwnershipHandover } from "TODO";
 *
 * const transaction = requestOwnershipHandover();
 *
 * // Send the transaction
 * ...
 *
 * ```
 */
export function requestOwnershipHandover(
  options: BaseTransactionOptions
) {
  return prepareContractCall({
    contract: options.contract,
    method: [
  "0x25692962",
  [],
  []
],
    params: []
  });
};


/**
 * Represents the parameters for the "transferOwnership" function.
 */
export type TransferOwnershipParams = {
  newOwner: AbiParameterToPrimitiveType<{"internalType":"address","name":"newOwner","type":"address"}>
};

/**
 * Calls the "transferOwnership" function on the contract.
 * @param options - The options for the "transferOwnership" function.
 * @returns A prepared transaction object.
 * @example
 * ```
 * import { transferOwnership } from "TODO";
 *
 * const transaction = transferOwnership({
 *  newOwner: ...,
 * });
 *
 * // Send the transaction
 * ...
 *
 * ```
 */
export function transferOwnership(
  options: BaseTransactionOptions<TransferOwnershipParams>
) {
  return prepareContractCall({
    contract: options.contract,
    method: [
  "0xf2fde38b",
  [
    {
      "internalType": "address",
      "name": "newOwner",
      "type": "address"
    }
  ],
  []
],
    params: [options.newOwner]
  });
};


